import * as Sentry from "@sentry/react";

/**
 * Manually capture an exception to Sentry
 * @param {Error} error - The error object to capture
 * @param {Object} context - Additional context to send with the error
 */
export const captureException = (error, context = {}) => {
  Sentry.captureException(error, {
    extra: context,
  });
};

/**
 * Manually capture a message to Sentry
 * @param {string} message - The message to capture
 * @param {string} level - The severity level (info, warning, error, fatal)
 */
export const captureMessage = (message, level = "info") => {
  Sentry.captureMessage(message, level);
};

/**
 * Set user context for Sentry
 * @param {Object} user - User information (id, email, username, etc.)
 */
export const setUser = (user) => {
  Sentry.setUser(user);
};

/**
 * Clear user context from Sentry
 */
export const clearUser = () => {
  Sentry.setUser(null);
};

/**
 * Add breadcrumb for debugging
 * @param {string} message - Breadcrumb message
 * @param {string} category - Category of the breadcrumb
 * @param {string} level - Severity level
 */
export const addBreadcrumb = (message, category = "custom", level = "info") => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
  });
};

/**
 * Test function to trigger a Sentry error (for testing purposes only)
 * Use this in development to verify Sentry is working
 */
export const testSentryError = () => {
  throw new Error("This is a test error for Sentry integration");
};

/**
 * Wrap async functions to automatically capture errors
 * @param {Function} fn - The async function to wrap
 * @returns {Function} - Wrapped function
 */
export const withSentryErrorHandling = (fn) => {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error) {
      captureException(error, {
        functionName: fn.name,
        arguments: args,
      });
      throw error;
    }
  };
};
